import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowRight, Users, Leaf, GraduationCap, HeartPulse, Clock, MapPin, Sparkles, Quote, Globe, Building2 } from 'lucide-react';
import { useAuth } from '@/App';
import axios from 'axios';
import { API } from '@/App';
import { usePageMeta } from '@/hooks/usePageMeta';

const LandingPage = () => {
  const { user } = useAuth();
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  usePageMeta({
    title: 'Talent Collective | Your Skills Can Change Millions of Lives',
    description: 'Connect your skills with India\'s most impactful organizations. Join the Talent Collective by ACT Grants x The Product Folks.'
  });

  useEffect(() => {
    fetchFeaturedProjects();
  }, []);

  const fetchFeaturedProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects?featured_only=true`);
      setFeaturedProjects(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const impactAreas = [
    {
      icon: GraduationCap,
      title: 'Education',
      description: 'Quality learning for every child in India.',
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-200',
      stat: '18+',
      statLabel: 'Organizations'
    },
    {
      icon: HeartPulse,
      title: 'Health',
      description: 'Accessible healthcare for all communities.',
      color: 'from-rose-500 to-rose-600',
      bgColor: 'bg-rose-50',
      textColor: 'text-rose-600',
      borderColor: 'border-rose-200',
      stat: '24+',
      statLabel: 'Organizations'
    },
    {
      icon: Leaf,
      title: 'Environment',
      description: 'A greener, cleaner tomorrow for India.',
      color: 'from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-600',
      borderColor: 'border-emerald-200',
      stat: '19+',
      statLabel: 'Organizations'
    },
    {
      icon: Users,
      title: "Women's Empowerment",
      description: 'Empowering women across the workforce.',
      color: 'from-violet-500 to-violet-600',
      bgColor: 'bg-violet-50',
      textColor: 'text-violet-600',
      borderColor: 'border-violet-200',
      stat: '3+',
      statLabel: 'Organizations'
    }
  ];

  const functionColors = {
    product: 'bg-teal-100 text-teal-700',
    design: 'bg-violet-100 text-violet-700',
    engineering: 'bg-blue-100 text-blue-700',
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Header />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white" data-testid="hero-section">
        <div className="absolute top-0 left-0 right-0 h-1 bg-teal-600" />
        {/* Decorative blurs - hidden on mobile to prevent black screen */}
        <div className="hidden md:block absolute top-20 right-0 w-96 h-96 bg-teal-100/40 rounded-full blur-3xl" />
        <div className="hidden md:block absolute bottom-0 left-0 w-80 h-80 bg-emerald-100/30 rounded-full blur-3xl" />
        
        <div className="relative container-custom pt-12 pb-16 sm:pt-16 sm:pb-24 md:pt-20 md:pb-32">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-0">
            {/* ACT x The Product Folks */}
            <div className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2 sm:py-3 rounded-full bg-gray-50 border border-gray-200 mb-6 sm:mb-8 animate-fade-in">
              <img src="https://actgrants.in/wp-content/themes/act/images/logo.png" alt="ACT Grants" className="h-5 sm:h-6 object-contain" />
              <span className="text-gray-400 text-xs sm:text-sm font-medium">x</span>
              <span className="text-xs sm:text-sm font-bold text-gray-700 tracking-tight">The Product Folks</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-[1.15] mb-4 sm:mb-6 animate-slide-up">
              Your skills can change
              <span className="block mt-1 sm:mt-2 text-teal-700">
                millions of lives.
              </span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed animate-slide-up stagger-1">
              Work with 64+ mission-driven organizations tackling India's biggest challenges 
              in education, health, environment, and women's empowerment.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up stagger-2">
              <Link to={user ? '/projects' : '/signup'}>
                <Button className="w-full sm:w-auto h-12 sm:h-14 px-8 sm:px-10 text-base font-semibold bg-teal-700 hover:bg-teal-800 text-white rounded-full shadow-lg shadow-teal-700/25 transition-all duration-300 hover:scale-105" data-testid="hero-cta-talent">
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            
            {/* Trust indicators */}
            <div className="mt-10 sm:mt-16 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4 sm:gap-8 text-sm text-gray-500 animate-fade-in stagger-3">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-teal-600" />
                <span>64+ organizations.</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-teal-600" />
                <span>1 Billion+ lives impacted.</span>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-teal-600" />
                <span>4 impact verticals.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Areas */}
      <section className="section-padding bg-gray-50" data-testid="impact-areas-section">
        <div className="container-custom px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-teal-700 mb-4">Four pillars of lasting impact</h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600">
              Explore opportunities with organizations creating real, measurable change across India.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {impactAreas.map((area, i) => (
              <Card 
                key={i} 
                className={`group relative overflow-hidden p-5 sm:p-6 bg-white border-2 ${area.borderColor} hover:border-teal-300 shadow-sm hover:shadow-xl hover:shadow-teal-100/50 transition-all duration-500 cursor-pointer rounded-2xl`}
                data-testid={`impact-area-${area.title.toLowerCase().replace(/['\s]+/g, '-')}`}
              >
                <div className="relative">
                  <div className={`relative h-12 w-12 sm:h-16 sm:w-16 rounded-xl sm:rounded-2xl bg-gradient-to-br ${area.color} flex items-center justify-center mb-4 sm:mb-5 group-hover:scale-110 transition-transform duration-500 shadow-lg`}>
                    <area.icon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  </div>
                  
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">{area.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">{area.description}</p>
                  
                  <div className="pt-4 border-t border-gray-100 flex items-center">
                    <span className="text-xl sm:text-2xl font-bold text-gray-800">{area.stat}</span>
                    <span className="text-sm text-gray-500 ml-2">{area.statLabel}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-white" data-testid="how-it-works-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-teal-100 text-teal-700 text-sm font-semibold mb-4">
                Simple Process
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Your path to
                <span className="text-teal-700"> meaningful work.</span>
              </h2>
              <p className="text-base md:text-lg text-gray-600 mb-10 leading-relaxed">
                We've made it simple to connect your expertise with organizations that share your passion for creating impact.
              </p>

              <div className="space-y-0">
                {[
                  { 
                    step: '01', 
                    title: 'Share Your Story', 
                    desc: 'Tell us about your skills, experience, and the causes close to your heart.',
                    color: 'bg-teal-700'
                  },
                  { 
                    step: '02', 
                    title: 'Discover Your Match', 
                    desc: 'Browse meaningful opportunities from 64+ mission-driven organizations.',
                    color: 'bg-teal-600'
                  },
                  { 
                    step: '03', 
                    title: 'Apply with Purpose', 
                    desc: 'Share why their mission resonates with you and how you can help.',
                    color: 'bg-teal-500'
                  },
                  { 
                    step: '04', 
                    title: 'Create Impact Together', 
                    desc: "Join passionate teams building solutions for India's future.",
                    color: 'bg-emerald-600'
                  }
                ].map((item, i) => (
                  <div key={i} className="flex gap-5 group">
                    <div className="relative flex flex-col items-center">
                      <div className={`h-14 w-14 rounded-2xl ${item.color} flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <span className="text-white font-bold text-sm">{item.step}</span>
                      </div>
                      {i < 3 && (
                        <div className="w-0.5 h-8 bg-gray-200 my-1" />
                      )}
                    </div>
                    <div className="pt-2 pb-6">
                      <h4 className="font-semibold text-gray-800 text-lg mb-1">{item.title}</h4>
                      <p className="text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Visual Element - Clean, no random floating elements */}
            <div className="relative">
              <div className="aspect-square rounded-3xl bg-gradient-to-br from-teal-50 via-emerald-50 to-gray-50 p-8 lg:p-12">
                <div className="h-full w-full rounded-2xl bg-white shadow-2xl p-6 flex flex-col">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-3 w-3 rounded-full bg-teal-400" />
                    <div className="h-3 w-3 rounded-full bg-emerald-400" />
                    <div className="h-3 w-3 rounded-full bg-gray-300" />
                  </div>
                  
                  <div className="space-y-4 flex-1">
                    {[
                      { cat: 'Education', icon: GraduationCap, bg: 'bg-blue-50', color: 'text-blue-600', org: 'Pratham' },
                      { cat: 'Health', icon: HeartPulse, bg: 'bg-rose-50', color: 'text-rose-600', org: 'Intelehealth' },
                      { cat: 'Environment', icon: Leaf, bg: 'bg-emerald-50', color: 'text-emerald-600', org: 'Hasiru Dala' }
                    ].map((item, i) => (
                      <div key={i} className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`h-10 w-10 rounded-xl ${item.bg} flex items-center justify-center`}>
                            <item.icon className={`h-5 w-5 ${item.color}`} />
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-800">{item.org}</div>
                            <div className="text-xs text-gray-500">{item.cat}</div>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <div className="h-5 w-16 bg-teal-100 rounded-full" />
                          <div className="h-5 w-20 bg-gray-100 rounded-full" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Companies */}
      <section className="py-16 bg-white border-y border-gray-100" data-testid="portfolio-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-teal-100 text-teal-700 text-sm font-semibold mb-4">
              ACT Portfolio
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-teal-700 mb-4 px-4">Organizations Creating Impact</h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Work with leading social impact startups from the ACT Grants portfolio.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 mb-10">
            {[
              { name: 'Pratham', sector: 'Education', logo: 'https://www.pratham.org/wp-content/uploads/2025/01/Pratham-Logo-7.gif', website: 'https://pratham.org' },
              { name: 'Rocket Learning', sector: 'Education', logo: 'https://rocketlearning.org/wp-content/uploads/Rocket-Learning_New-Logo-2.png', website: 'https://rocketlearning.org' },
              { name: 'Karya', sector: 'Education', logo: 'https://imagedelivery.net/zZi_VLBckmtLzucvU2-pnQ/f88f4c03-5915-4781-ae30-768a2168da00/w=128,q=75', website: 'https://karya.in' },
              { name: 'Wysa', sector: 'Health', logo: 'https://cdn.wysa.io/logos/logo.png', website: 'https://wysa.com' },
              { name: 'Khushi Baby', sector: 'Health', logo: 'https://cdn.prod.website-files.com/665fe4f280c6b2f1935e282a/66684116cd3010f1b0c7074c_logo-colored.png', website: 'https://khushibaby.org' },
              { name: 'Niramai', sector: 'Health', logo: 'https://niramai.com/wp-content/uploads/2025/05/Logoonly.png', website: 'https://niramai.com' },
              { name: 'Intelehealth', sector: 'Health', logo: 'https://intelehealth.org/wp-content/themes/intelehealth/images/logo.png', website: 'https://intelehealth.org' },
              { name: 'Saahas', sector: 'Environment', logo: 'https://saahas.org/wp-content/uploads/2024/04/Saahas-logo.svg', website: 'https://saahas.org' },
              { name: 'Hasiru Dala', sector: 'Environment', logo: 'https://hasirudalainnovations.com/img/logo/Hasiru-Dala-Innovations-Hi-Res-Logo.png', website: 'https://hasirudala.in' },
            ].map((company, i) => (
              <a 
                key={i}
                href={company.website}
                target="_blank"
                rel="noopener noreferrer" 
                className="bg-gray-50 rounded-2xl p-5 flex flex-col items-center justify-center h-36 hover:shadow-lg transition-all duration-300 group cursor-pointer border border-transparent hover:border-teal-200 hover:bg-white"
                data-testid={`company-logo-${company.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="h-14 w-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                  <img 
                    src={company.logo} 
                    alt={`${company.name} logo`}
                    className="max-h-14 max-w-full object-contain"
                    loading="lazy"
                  />
                </div>
                <span className="text-xs font-semibold text-gray-700 text-center leading-tight">{company.name}</span>
                <span className="text-[10px] text-gray-500 mt-0.5">{company.sector}</span>
              </a>
            ))}
          </div>

          <div className="text-center">
            <p className="text-gray-500 text-sm mb-4">And 50+ more organizations across India.</p>
            <a 
              href="https://actgrants.in" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700 transition-colors"
            >
              View ACT Grants Portfolio
              <ArrowRight className="ml-1 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="section-padding bg-gray-900" data-testid="featured-projects-section">
        <div className="container-custom px-4 sm:px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 sm:mb-12">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-teal-500/20 text-teal-400 text-sm font-semibold mb-4">
                Open Opportunities
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">Featured Projects</h2>
              <p className="text-gray-400 text-sm sm:text-base">Current openings from our portfolio organizations.</p>
            </div>
            <Link to="/projects" className="mt-4 md:mt-0">
              <Button variant="outline" className="text-white border-gray-600 hover:bg-gray-800 rounded-full" data-testid="view-all-projects">
                Browse Opportunities
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="p-5 sm:p-6 bg-gray-800/50 border-gray-700 animate-pulse rounded-2xl">
                  <div className="h-4 bg-gray-700 rounded w-3/4 mb-4" />
                  <div className="h-3 bg-gray-700 rounded w-1/2 mb-6" />
                  <div className="h-20 bg-gray-700 rounded mb-4" />
                </Card>
              ))}
            </div>
          ) : featuredProjects.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-6">
              {featuredProjects.map((project) => (
                <Link to={`/projects/${project.id}`} key={project.id}>
                  <Card className="group p-6 bg-gray-800/50 border-gray-700 hover:bg-gray-800 hover:border-teal-500/50 transition-all duration-300 h-full cursor-pointer rounded-2xl" data-testid={`project-card-${project.id}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="text-lg font-semibold text-white mb-1 group-hover:text-teal-400 transition-colors">
                          {project.title}
                        </h4>
                        <p className="text-sm text-gray-400">{project.company_name}</p>
                      </div>
                    </div>
                    <p className="text-gray-300 text-sm line-clamp-3 mb-4 leading-relaxed">
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-auto">
                      <span className={`${functionColors[project.function]} px-3 py-1 rounded-full text-xs font-semibold capitalize`}>
                        {project.function}
                      </span>
                      <span className="bg-gray-700 text-gray-300 px-3 py-1 rounded-full text-xs flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {project.hours_per_week}h/week
                      </span>
                      <span className="bg-gray-700 text-gray-300 px-3 py-1 rounded-full text-xs flex items-center capitalize">
                        <MapPin className="h-3 w-3 mr-1" />
                        {project.location_type}
                      </span>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center bg-gray-800/50 border-gray-700 rounded-2xl">
              <p className="text-gray-400">No featured projects available at the moment.</p>
              <Link to="/projects" className="text-teal-400 hover:underline mt-2 inline-block">
                Browse all projects.
              </Link>
            </Card>
          )}
        </div>
      </section>

      {/* Quote Section */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="absolute top-10 left-10 w-64 h-64 bg-teal-100/30 rounded-full blur-3xl" />
        
        <div className="container-custom relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-teal-700 mb-8 shadow-lg shadow-teal-700/25">
              <Quote className="h-8 w-8 text-white" />
            </div>
            <blockquote className="text-2xl md:text-3xl font-medium text-gray-800 leading-relaxed mb-8">
              "Never doubt that a group of thoughtful, committed people can change the world. 
              <span className="text-teal-700 font-bold"> It's the only thing that ever has.</span>"
            </blockquote>
            <p className="text-gray-500 font-medium">- Margaret Mead</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
